import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Registration, EventSettings } from '../types';
import { dbService } from '../firebaseConfig';
import { compressImage } from '../utils/compressImage';

interface PaymentPageProps {
  registration: Registration;
  onPaymentConfirmed: (updated?: Registration) => void;
}

const MAX_RECEIPT_MB = 10;

const PaymentPage: React.FC<PaymentPageProps> = ({ registration, onPaymentConfirmed }) => {
  const [confirming, setConfirming] = useState(false);
  const [copied, setCopied] = useState(false);
  const [settings, setSettings] = useState<EventSettings | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
  const [paymentMethodOverride, setPaymentMethodOverride] = useState<'pix' | 'credit_card' | null>(null);

  // Comprovante
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [receiptPreview, setReceiptPreview] = useState<string | null>(null);
  const [originalSize, setOriginalSize] = useState<number | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sendViaWhats, setSendViaWhats] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const currentPaymentMethod = paymentMethodOverride || registration.paymentMethod;

  useEffect(() => {
    dbService.getSettings().then(setSettings).catch(() => setErrorMsg("Erro ao carregar configurações."));
  }, []);

  useEffect(() => {
    return () => { if (receiptPreview) URL.revokeObjectURL(receiptPreview); };
  }, [receiptPreview]);

  const handleRedirectPayment = async () => {
    setConfirming(true);
    setErrorMsg(null);
    try {
      const result = await dbService.createCheckoutRedirect({
        registrationId: registration.id,
        amount: registration.amount,
        fullName: registration.fullName,
        email: registration.email
      });

      if (result.data && result.data.url) {
        setRedirectUrl(result.data.url);
        window.location.href = result.data.url;
      } else if (result.data && result.data.error) {
        setErrorMsg(result.data.error);
        setConfirming(false);
      } else {
        setErrorMsg("Não foi possível gerar o link de pagamento. Por favor, tente novamente ou use PIX.");
        setConfirming(false);
      }
    } catch (err: any) {
      setErrorMsg("Falha de conexão. Verifique sua internet.");
      setConfirming(false);
    }
  };

  const pixPayload = useMemo(() => {
    if (!settings) return "";

    const key = (settings.pixKey || '78118353000188').replace(/\D/g, '');
    const receiver = (settings.pixReceiver || 'IPCC').normalize("NFD").replace(/[̀-ͯ]/g, "").toUpperCase().substring(0, 25);
    const amount = registration.amount.toFixed(2);

    const f = (id: string, val: string) => `${id}${val.length.toString().padStart(2, '0')}${val}`;

    const gui = f("00", "BR.GOV.BCB.PIX");
    const keyField = f("01", key);
    const merchantAccountInfo = f("26", gui + keyField);

    const payloadBase = [
      "000201",
      merchantAccountInfo,
      "52040000",
      "5303986",
      f("54", amount),
      "5802BR",
      f("59", receiver),
      f("60", "CASCAVEL"),
      f("62", f("05", "***")),
      "6304"
    ].join('');

    const crc16 = (data: string) => {
      let crc = 0xFFFF;
      for (let i = 0; i < data.length; i++) {
        crc ^= data.charCodeAt(i) << 8;
        for (let j = 0; j < 8; j++) {
          if ((crc & 0x8000) !== 0) crc = (crc << 1) ^ 0x1021;
          else crc <<= 1;
        }
      }
      return (crc & 0xFFFF).toString(16).toUpperCase().padStart(4, '0');
    };

    return payloadBase + crc16(payloadBase);
  }, [settings, registration.amount]);

  const copyPix = () => {
    navigator.clipboard.writeText(pixPayload);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  const handleReceiptSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const original = e.target.files?.[0];
    e.target.value = ''; // permite escolher o mesmo arquivo de novo
    if (!original) return;
    setErrorMsg(null);
    const isImage = original.type.startsWith('image/');
    const isPdf = original.type === 'application/pdf';
    if (!isImage && !isPdf) {
      setErrorMsg('Envie uma imagem (print/foto) ou um PDF do comprovante.');
      return;
    }
    if (isPdf && original.size > MAX_RECEIPT_MB * 1024 * 1024) {
      setErrorMsg(`O arquivo é muito grande. Envie um comprovante de até ${MAX_RECEIPT_MB} MB.`);
      return;
    }

    setPreparing(true);
    try {
      // Fotos de celular são grandes: reduz no navegador antes de enviar
      const file = isImage ? await compressImage(original) : original;
      if (file.size > MAX_RECEIPT_MB * 1024 * 1024) {
        setErrorMsg(`O arquivo é muito grande. Envie um comprovante de até ${MAX_RECEIPT_MB} MB.`);
        return;
      }
      setOriginalSize(original.size);
      setReceiptFile(file);
      setReceiptPreview(isImage ? URL.createObjectURL(file) : null);
      setSendViaWhats(false);
    } finally {
      setPreparing(false);
    }
  };

  const handleSendReceipt = async () => {
    if (!registration.id) return;
    setUploading(true);
    setErrorMsg(null);
    try {
      if (sendViaWhats) {
        await dbService.markReceiptViaWhatsApp(registration.id);
        onPaymentConfirmed({ ...registration, receiptViaWhatsApp: true });
        return;
      }
      if (!receiptFile) return;
      const patch = await dbService.uploadReceipt(registration.id, receiptFile);
      onPaymentConfirmed({ ...registration, ...patch });
    } catch (err) {
      console.error(err);
      setErrorMsg('Não foi possível enviar o comprovante. Verifique sua internet e tente novamente.');
    } finally {
      setUploading(false);
    }
  };

  const fmtMB = (bytes: number) => (bytes / 1024 / 1024).toFixed(2) + ' MB';
  const canFinish = sendViaWhats || !!receiptFile;

  if (!settings) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-sand-soft px-4">
      <div className="w-12 h-12 border-4 border-brand-sand border-t-brand-amber rounded-full animate-spin mb-6"></div>
      <p className="text-brand-stone text-sm italic">Preparando ambiente seguro...</p>
    </div>
  );

  const isPixUI = currentPaymentMethod === 'pix' || settings.pauseAutomaticPayments;

  return (
    <div className="min-h-screen bg-sand-soft py-12 px-4">
      <div className="max-w-lg mx-auto bg-white rounded-[40px] shadow-2xl overflow-hidden border border-brand-sand">
        <div className="p-10 text-center border-b bg-brand-sand/40">
          <p className="text-brand-amber font-black uppercase text-[10px] tracking-widest mb-4">Pagamento Seguro</p>
          <h2 className="font-display text-3xl font-semibold uppercase text-brand-dark">
            {isPixUI ? 'Pagar via PIX' : 'Cartão de Crédito'}
          </h2>
        </div>

        <div className="p-10">
          {errorMsg && (
            <div className="mb-8 p-6 bg-red-50 border border-red-100 text-red-700 rounded-3xl text-sm leading-relaxed">
              <p className="font-bold mb-1">Houve um problema:</p>
              <p className="opacity-80">{errorMsg}</p>
            </div>
          )}

          {isPixUI ? (
            <div className="flex flex-col items-center text-center space-y-6">
              {settings.paymentInfo && (
                <div className="w-full bg-brand-sand/60 border border-brand-sand rounded-2xl p-4 text-left flex items-start gap-3">
                  <span className="text-xl">💳</span>
                  <p className="text-brand-dark text-sm leading-snug font-medium">{settings.paymentInfo}</p>
                </div>
              )}

              {/* Passo 1 — pagar */}
              <div className="w-full text-left">
                <p className="text-[10px] font-black uppercase tracking-[0.3em] text-brand-ember mb-1">Passo 1</p>
                <p className="font-display text-xl uppercase text-brand-dark">Pague com o QR Code ou copie o código</p>
              </div>

              <div className="p-6 bg-white rounded-[40px] border-2 border-brand-sand shadow-xl relative group">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(pixPayload)}`}
                  className="w-56 h-56 rounded-xl"
                  alt="QR Code PIX"
                />
                <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-[40px] cursor-pointer" onClick={copyPix}>
                  <p className="text-brand-dark font-black text-xs uppercase tracking-widest">Clique para Copiar</p>
                </div>
              </div>

              <div className="w-full space-y-3">
                <div className="bg-brand-sand/60 p-4 rounded-2xl border border-brand-sand text-center">
                  <p className="text-[10px] font-black text-brand-dark uppercase tracking-[0.2em] mb-1">Valor Total</p>
                  <p className="text-3xl font-display font-semibold text-brand-dark">R$ {registration.amount.toFixed(2)}</p>
                </div>

                <button
                  onClick={copyPix}
                  className={`w-full py-5 rounded-2xl font-black uppercase tracking-widest transition-all flex items-center justify-center gap-3 ${copied ? 'bg-brand-green text-white shadow-green-100' : 'bg-brand-amber text-brand-night hover:bg-brand-dark hover:text-white shadow-brand-amber/20'} shadow-lg active:scale-95`}
                >
                  {copied ? (
                    <><span className="text-xl">✅</span> COPIADO!</>
                  ) : (
                    <><span className="text-xl">📋</span> COPIAR CÓDIGO PIX</>
                  )}
                </button>
              </div>

              {/* Passo 2 — anexar comprovante */}
              <div className="w-full pt-8 border-t border-brand-sand mt-4 text-left space-y-4">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.3em] text-brand-ember mb-1">Passo 2</p>
                  <p className="font-display text-xl uppercase text-brand-dark">Anexe o comprovante do pagamento</p>
                  <p className="text-brand-stone text-xs mt-1">Print ou foto do comprovante do seu banco (imagem ou PDF, até {MAX_RECEIPT_MB} MB). A organização vai conferir e confirmar sua inscrição.</p>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,application/pdf"
                  className="hidden"
                  onChange={handleReceiptSelect}
                />

                <div
                  onClick={() => !uploading && !preparing && !sendViaWhats && fileInputRef.current?.click()}
                  className={`w-full rounded-3xl border-2 border-dashed p-6 transition-all flex items-center gap-5 ${sendViaWhats ? 'opacity-40 cursor-not-allowed border-brand-sand bg-brand-sand/20' : receiptFile ? 'cursor-pointer border-brand-green bg-green-50/60' : 'cursor-pointer border-brand-sand bg-brand-sand/40 hover:border-brand-amber hover:bg-brand-amber/5'}`}
                >
                  <div className="w-20 h-20 rounded-2xl bg-white border border-brand-sand overflow-hidden flex items-center justify-center shrink-0 shadow-sm">
                    {preparing ? (
                      <div className="w-6 h-6 border-2 border-brand-sand border-t-brand-amber rounded-full animate-spin"></div>
                    ) : receiptPreview ? (
                      <img src={receiptPreview} alt="Comprovante" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-3xl">{receiptFile ? '📄' : '📎'}</span>
                    )}
                  </div>
                  <div className="min-w-0">
                    {preparing ? (
                      <>
                        <p className="font-bold text-brand-dark text-sm">Otimizando imagem...</p>
                        <p className="text-brand-stone text-xs">Só um instante</p>
                      </>
                    ) : receiptFile ? (
                      <>
                        <p className="font-bold text-brand-dark text-sm truncate">{receiptFile.name}</p>
                        <p className="text-brand-stone text-xs">
                          {fmtMB(receiptFile.size)}
                          {originalSize && originalSize > receiptFile.size * 1.2 ? ` (reduzida de ${fmtMB(originalSize)})` : ''}
                          {' '}• toque para trocar
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="font-bold text-brand-dark text-sm">Toque para selecionar o arquivo</p>
                        <p className="text-brand-stone text-xs">Foto, print ou PDF do comprovante</p>
                      </>
                    )}
                  </div>
                </div>

                {settings.contactWhatsApp && (
                  <label className={`flex items-start gap-3 p-4 rounded-2xl border cursor-pointer transition-all ${sendViaWhats ? 'border-brand-green bg-green-50/60' : 'border-brand-sand bg-white hover:bg-brand-sand/30'}`}>
                    <input
                      type="checkbox"
                      className="mt-0.5 w-5 h-5 accent-[#2F6B45] shrink-0"
                      checked={sendViaWhats}
                      onChange={e => {
                        setSendViaWhats(e.target.checked);
                        if (e.target.checked) { setReceiptFile(null); setReceiptPreview(null); setOriginalSize(null); }
                      }}
                    />
                    <span className="text-sm text-brand-dark leading-snug">
                      <b>Não consegui anexar o comprovante</b>, vou enviar pelo WhatsApp.
                      <span className="block text-xs text-brand-stone mt-0.5">Na próxima tela terá um botão que abre a conversa com a organização.</span>
                    </span>
                  </label>
                )}

                <button
                  onClick={handleSendReceipt}
                  disabled={!canFinish || uploading || preparing}
                  className="w-full bg-brand-dark text-white py-5 rounded-2xl font-black uppercase tracking-widest shadow-xl active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                >
                  {uploading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      ENVIANDO...
                    </>
                  ) : sendViaWhats ? 'FINALIZAR • ENVIAR PELO WHATSAPP' : 'ENVIAR COMPROVANTE • FINALIZAR'}
                </button>

                <p className="text-[10px] text-brand-stone uppercase font-bold tracking-widest italic leading-relaxed text-center pt-2">
                  O QR Code acima já inclui o valor correto.<br />A confirmação é feita pela organização após conferir o comprovante.
                </p>

                {settings.contactWhatsApp && (
                  <p className="text-center text-xs text-brand-stone">
                    Problemas com o envio?{' '}
                    <a
                      href={`https://wa.me/${settings.contactWhatsApp}?text=${encodeURIComponent(`Olá! Fiz o pagamento via PIX da inscrição ${registration.id?.slice(-8).toUpperCase()} (${registration.fullName}) e estou com dificuldade para anexar o comprovante no site.`)}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-brand-green font-bold underline underline-offset-4"
                    >
                      Fale com a organização no WhatsApp
                    </a>
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center space-y-8 py-6">
              {redirectUrl ? (
                <div className="space-y-6 animate-in fade-in zoom-in">
                  <div className="bg-green-50 p-6 rounded-3xl border border-green-100">
                    <p className="text-xs text-green-800 font-bold">Checkout Gerado!</p>
                    <p className="text-[10px] text-green-600 mt-1">Estamos te levando para o PagSeguro...</p>
                  </div>
                  <a href={redirectUrl} className="block w-full bg-brand-green text-white py-5 rounded-2xl font-black uppercase tracking-widest shadow-lg text-center">
                    Acessar PagSeguro Agora
                  </a>
                </div>
              ) : (
                <>
                  <div className="bg-brand-sand/60 p-6 rounded-3xl border border-brand-sand">
                    <p className="text-xs text-brand-dark leading-relaxed font-medium">
                      Você será redirecionado para o <b>PagSeguro</b> para pagar no cartão.
                    </p>
                  </div>
                  <button
                    disabled={confirming}
                    onClick={handleRedirectPayment}
                    className="w-full bg-brand-amber text-brand-night py-6 rounded-2xl font-black text-lg shadow-xl shadow-brand-amber/20 active:scale-95 transition-all flex items-center justify-center gap-3"
                  >
                    {confirming ? (
                      <>
                        <div className="w-5 h-5 border-2 border-brand-night/30 border-t-brand-night rounded-full animate-spin"></div>
                        CARREGANDO...
                      </>
                    ) : 'PAGAR COM CARTÃO'}
                  </button>
                </>
              )}
              <button onClick={() => setPaymentMethodOverride('pix')} className="text-[10px] font-black uppercase text-brand-stone hover:text-brand-amber transition-colors tracking-widest underline underline-offset-4">Usar PIX em vez de Cartão</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
